import { useEffect, useMemo, useState, type CSSProperties } from 'react';

/** Mesma ideia do carnaval: confetes pequenos, círculos e quadrados; cores da marca + destaques. */
const CONFETTI_COLORS = [
	'bg-brand-orange',
	'bg-brand-pink',
	'bg-brand-purple',
	'bg-amber-300',
	'bg-cyan-400',
];

type Origin = { leftPct: number; topPct: number; burstDelaySec: number };

function buildBurst(
	count: number,
	origin: Origin,
): Array<{
	tx: number;
	ty: number;
	duration: number;
	delay: number;
	size: number;
	colorClass: string;
	isCircle: boolean;
	rotation: number;
	leftPct: number;
	topPct: number;
	burstDelaySec: number;
}> {
	const { leftPct, topPct, burstDelaySec } = origin;
	const items: ReturnType<typeof buildBurst> = [];
	for (let i = 0; i < count; i++) {
		const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 1.1;
		const power = 90 + Math.random() * 240;
		const tx = Math.cos(angle) * power;
		const ty = Math.sin(angle) * power;
		items.push({
			tx,
			ty,
			duration: 1.69 + Math.random() * 1.31,
			delay: Math.random() * 0.35,
			size: 4 + Math.random() * 6,
			colorClass: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
			isCircle: i % 2 === 0,
			rotation: 180 + Math.random() * 900,
			leftPct,
			topPct,
			burstDelaySec,
		});
	}
	return items;
}

const WelcomeFireworks = () => {
	const [visible, setVisible] = useState(true);

	const particles = useMemo(() => {
		const origins: Origin[] = [
			{ leftPct: 50, topPct: 32, burstDelaySec: 0 },
			{ leftPct: 22, topPct: 48, burstDelaySec: 0.53 },
			{ leftPct: 78, topPct: 46, burstDelaySec: 0.9 },
			{ leftPct: 50, topPct: 58, burstDelaySec: 1.31 },
		];
		return origins.flatMap((o) => buildBurst(42, o));
	}, []);

	useEffect(() => {
		/* cobre último burst + delay máx. + duração máx. da animação + folga */
		const id = window.setTimeout(() => setVisible(false), 6500);
		return () => window.clearTimeout(id);
	}, []);

	if (!visible) return null;

	return (
		<div
			className="pointer-events-none fixed inset-0 z-[200] overflow-hidden"
			aria-hidden
		>
			<style>{`
				/* 0% invisível: com animation-fill backwards, vale durante o delay (nada aparece antes do estouro) */
				@keyframes welcome-firework-burst {
					0% {
						transform: translate(-50%, -50%) translate(0, 0) rotate(0deg) scale(1);
						opacity: 0;
					}
					1% {
						opacity: 1;
					}
					55% {
						opacity: 0.92;
					}
					100% {
						transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0.15);
						opacity: 0;
					}
				}
			`}</style>
			{particles.map((p, i) => (
				<div
					key={i}
					className={`absolute shadow-sm ${p.colorClass} ${
						p.isCircle ? 'rounded-full' : 'rounded-sm'
					}`}
					style={
						{
							left: `${p.leftPct}%`,
							top: `${p.topPct}%`,
							width: p.size,
							height: p.size,
							'--tx': `${p.tx}px`,
							'--ty': `${p.ty}px`,
							'--rot': `${p.rotation}deg`,
							animation: `welcome-firework-burst ${
								p.duration
							}s cubic-bezier(0.08, 0.72, 0.18, 1) ${
								p.burstDelaySec + p.delay
							}s both`,
							willChange: 'transform, opacity',
						} as CSSProperties
					}
				/>
			))}
		</div>
	);
};

export { WelcomeFireworks };
